import React, { useRef } from 'react';

import { LinearProgress, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import type { QuestionRunProgress } from './analysis-question-run';

export const QUESTION_RUN_PROGRESS_LABEL = 'Extraction question run progress';

export type QuestionRunBarState = {
  /** Null until the first poll lands. */
  progress: QuestionRunProgress | null;
  /** What was queued, shown until the first poll reports its own total. */
  total: number;
  /** The run is over: the bar holds its final count. */
  finished?: boolean;
};

const useStyles = makeStyles()(theme => ({
  bar: {
    backgroundColor: theme.palette.grey[200],
    borderRadius: 3,
    height: 6,
    marginTop: theme.spacing(0.75),
  },
  caption: {
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
  footer: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'space-between',
    marginTop: theme.spacing(0.5),
    minHeight: 24,
  },
  root: {
    maxWidth: 320,
  },
}));

/**
 * How far an approved question run has got, and its final count once it is
 * over. `action` sits on the caption row, e.g. a Stop button. Renders nothing
 * without a run.
 */
export const QuestionRunProgressBar = React.memo(function QuestionRunProgressBar({
  action,
  wait,
}: {
  wait: QuestionRunBarState | null;
  action?: React.ReactNode;
}) {
  const { classes } = useStyles();
  const display = useQuestionRunBarDisplay(wait);

  if (!display) return null;

  return (
    <div className={classes.root}>
      <LinearProgress
        aria-label={QUESTION_RUN_PROGRESS_LABEL}
        className={classes.bar}
        value={display.percent}
        variant="determinate"
      />
      {Boolean(display.caption || action) && (
        <div className={classes.footer}>
          {Boolean(display.caption) && (
            <Typography className={classes.caption} component="span">
              {display.caption}
            </Typography>
          )}
          {action}
        </div>
      )}
    </div>
  );
});

/**
 * What a question run bar shows. The bar stays determinate so it never
 * switches modes: it sits at 0 with no `caption` until a poll reports a total,
 * then `caption` counts documents.
 */
export function useQuestionRunBarDisplay(wait: QuestionRunBarState | null) {
  /**
   * A poll that lands out of order must not move the bar back. Reset between
   * runs, or the next run starts at the last one's count.
   */
  const maxDoneRef = useRef(0);
  maxDoneRef.current = wait ? Math.max(maxDoneRef.current, wait.progress?.done ?? 0) : 0;

  if (!wait) return null;
  const { progress } = wait;
  if (!progress) return { caption: null, percent: 0 };

  const total = progress.total || wait.total;
  if (!total && !wait.finished) return { caption: null, percent: 0 };
  const done = Math.min(wait.finished ? progress.done : maxDoneRef.current, total);
  return {
    caption: `${done} of ${total} ${total === 1 ? 'document' : 'documents'}`,
    percent: total ? (done / total) * 100 : 0,
  };
}
